import { Controller } from "@hotwired/stimulus";

// Conta a leitura da matéria e mede o engajamento (docs/metrics.md). O token vem assinado do
// servidor com a origem do leitor; aqui só se decide QUANDO a leitura existe e quanto durou.
//
// - Não conta prévia do cache do Turbo (html[data-turbo-preview]): a resposta de verdade chega em
//   seguida e conecta de novo.
// - Não conta aba aberta em segundo plano nem prerender do navegador até o leitor olhar para ela.
// - Manda três beacons com o mesmo token: ao aparecer, ao virar engajada e ao sair. O do meio
//   existe porque o de saída falha com frequência em WebView e no celular (o sistema mata a aba
//   sem avisar), e é ele que garante a contagem de engajada.
//
// O token só é lido no connect. O layout público usa morph no refresh, e aí o <article> continua o
// mesmo: a leitura que já começou segue com o token dela — e o atributo também fica com ele, senão
// o snapshot do Turbo guardaria o token novo e a volta pelo cache começaria outra leitura.
//
// Voltar para a matéria pelo cache do Turbo (botão voltar) traz o mesmo token, e é a mesma leitura.
// Como o servidor só guarda o maior valor de cada beacon, o tempo e a rolagem dela ficam aqui,
// por token, e o controller retoma de onde parou: 10 s, sai, volta e lê mais 10 s são 20 s, e o
// tempo fora da matéria não conta. Vive enquanto a aba vive (visitas Turbo não recarregam o JS).
const readings = new Map();
const MAX_READINGS = 50;

export default class extends Controller {
  static targets = ["body"];
  static values = {
    token: String,
    url: String,
    engagedSeconds: { type: Number, default: 15 },
    engagedScroll: { type: Number, default: 50 },
  };

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return;

    this.token = this.tokenValue;
    this.tracking = true;
    this.visibleSince = null;
    const saved = readings.get(this.token);
    this.started = saved?.started ?? false;
    this.engagedSent = saved?.engagedSent ?? false;
    this.activeMs = saved?.activeMs ?? 0;
    this.maxDepth = saved?.maxDepth ?? 0;

    this.onVisibility = () => this.visibilityChanged();
    this.onScroll = () => this.scrolled();
    this.onPageHide = () => this.leave();
    this.onMorphAttribute = (event) => {
      if (event.detail.attributeName === "data-post-read-token-value") event.preventDefault();
    };

    document.addEventListener("visibilitychange", this.onVisibility);
    document.addEventListener("prerenderingchange", this.onVisibility);
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("pagehide", this.onPageHide);
    this.element.addEventListener("turbo:before-morph-attribute", this.onMorphAttribute);

    this.visibilityChanged();
  }

  disconnect() {
    if (!this.tracking) return;

    this.leave();
    this.tracking = false;
    clearTimeout(this.engagedTimer);
    cancelAnimationFrame(this.scrollFrame);
    document.removeEventListener("visibilitychange", this.onVisibility);
    document.removeEventListener("prerenderingchange", this.onVisibility);
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("pagehide", this.onPageHide);
    this.element.removeEventListener("turbo:before-morph-attribute", this.onMorphAttribute);
  }

  visibilityChanged() {
    if (document.prerendering) return;

    if (document.visibilityState === "visible") {
      this.visibleSince ??= performance.now();
      if (!this.started) this.start();
      this.save();
      this.scheduleEngagedCheck();
    } else {
      this.leave();
    }
  }

  start() {
    this.started = true;
    this.measureScroll();
    this.send();
  }

  // Aba escondida, página fechada ou navegação para outra página: pausa o relógio e manda o que
  // foi medido até aqui. Pode acontecer mais de uma vez na mesma leitura (troca de aba e volta).
  leave() {
    if (!this.started) return;

    this.pause();
    this.send();
  }

  pause() {
    clearTimeout(this.engagedTimer);
    if (this.visibleSince !== null) {
      this.activeMs += performance.now() - this.visibleSince;
      this.visibleSince = null;
    }
    this.save();
  }

  // O que já foi medido nesta leitura, para uma volta pelo cache do Turbo continuar a conta.
  save() {
    readings.delete(this.token);
    readings.set(this.token, {
      started: this.started, engagedSent: this.engagedSent, activeMs: this.activeMs, maxDepth: this.maxDepth,
    });
    if (readings.size > MAX_READINGS) readings.delete(readings.keys().next().value);
  }

  get activeSeconds() {
    const running = this.visibleSince === null ? 0 : performance.now() - this.visibleSince;
    return Math.floor((this.activeMs + running) / 1000);
  }

  get engaged() {
    return this.activeSeconds >= this.engagedSecondsValue && this.maxDepth >= this.engagedScrollValue;
  }

  scheduleEngagedCheck() {
    if (this.engagedSent) return;

    clearTimeout(this.engagedTimer);
    const remainingMs = this.engagedSecondsValue * 1000 - this.activeSeconds * 1000;
    this.engagedTimer = setTimeout(() => this.checkEngaged(), Math.max(remainingMs, 0) + 250);
  }

  checkEngaged() {
    if (this.engagedSent || !this.engaged) return;

    this.engagedSent = true;
    this.save();
    this.send();
  }

  scrolled() {
    cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      this.measureScroll();
      this.checkEngaged();
    });
  }

  // Quanto do corpo da matéria já passou pela tela, de 0 a 100. Mede o corpo e não a página: o
  // rodapé, os anúncios e os relacionados não são a matéria.
  measureScroll() {
    const body = this.hasBodyTarget ? this.bodyTarget : this.element;
    const rect = body.getBoundingClientRect();
    const seen = window.innerHeight - rect.top;
    const depth = rect.height > 0 ? Math.round((seen / rect.height) * 100) : 100;

    this.maxDepth = Math.max(this.maxDepth, Math.min(100, Math.max(0, depth)));
  }

  // sendBeacon sobrevive ao fechamento da aba e manda como formulário, que o Rails lê sem header
  // nenhum. Se o navegador recusar (fila cheia), vai por fetch com keepalive.
  send() {
    if (!this.urlValue || !this.token) return;

    const body = new URLSearchParams({ token: this.token, s: this.activeSeconds, d: this.maxDepth });
    if (navigator.sendBeacon?.(this.urlValue, body)) return;

    fetch(this.urlValue, { method: "POST", body, keepalive: true, credentials: "same-origin" }).catch(() => {});
  }
}
